import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { CampaignManager } from "../../../core/storage/CampaignManager";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import { Campaign } from "../../../ducks/campaign";
import { couponActions } from "../../../ducks/coupon";
import { menuActions } from "../../../ducks/menu";
import { Store, storeActions } from "../../../ducks/store";
import { useCoupon } from "../../../hooks/useCoupon";
import { useEarlyBird } from "../../../hooks/useEarlyBird";
import { useMenu } from "../../../hooks/useMenu";
import { useNavigation } from "../../../hooks/useNavigation";
import { useOrder } from "../../../hooks/useOrder";
import { useStore } from "../../../hooks/useStore";
import { formatDateTime } from "../../../utils/date";

export const useOrderComplete = () => {
  const dispatch = useDispatch();
  const { orderId } = useParams<{ orderId: string }>();
  const { orderHistories } = useOrder(orderId);
  const { setFavoriteStore } = useStore();
  const { setMultipleFavoriteMenus } = useMenu(useCoupon());
  const { navigateToTopPage, navigateToPage } = useNavigation();
  const { checkCampaign } = useEarlyBird();

  const [store, setStore] = useState<Store | null>(null);
  // useEffect で clearCampaignMode が呼ばれる前に取得
  const [isStockItemMode] = useState(() => CampaignManager.isStockItemMode());

  const order = useMemo(() => {
    return orderHistories.find((order) => order.id === orderId);
  }, [orderHistories]);

  const pickupDateText = useMemo(() => {
    if (!order) return "";

    return formatDateTime(new Date(order.pickupDate), true);
  }, [order]);

  const campaign = LocalStorageManager.Instance.getObject<Campaign>(
    AppConfig.Instance.LocalStorageKey["campaign"],
  );

  useEffect(() => {
    dispatch(menuActions.setMenuInCart.started({ menus: [] }));
    dispatch(couponActions.clearCoupons.started({}));

    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["menusInCart"],
    );
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["campaign"],
    );
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["latestOrderId"],
    );
    CampaignManager.clearCampaignMode();
  }, []);

  useEffect(() => {
    if (!order) return;

    const storeId = order.store.id;
    const storeName = order.store.name;

    dispatch(appActions.showLoading());
    dispatch(
      storeActions.getStores.started({
        conditions: [
          {
            name: "店舗名",
            operator: "equal",
            value: storeName,
          },
        ],
        onComplete: () => dispatch(appActions.hideLoading()),
        onSuccess: (stores) => {
          stores.some((store) => {
            if (store.id === storeId) {
              setStore(store);
              return true;
            }
            return false;
          });
        },
      }),
    );
  }, [order]);

  const handleSetFavoriteStore = useCallback(
    (favorite: boolean) => {
      if (!store) return;

      setFavoriteStore(store.id, favorite, () =>
        setStore({ ...store, isFavorite: favorite }),
      );
    },
    [store],
  );

  const handleSetFavoriteMenu = useCallback(() => {
    if (!order) return;

    const menuNames = order.menus.map((menu) => menu.name);
    const menuCodes = order.menus.map((menu) => menu.code);

    setMultipleFavoriteMenus(menuCodes, true, () => {
      dispatch(
        appActions.showModal.started({
          title: "下記をお気に入り登録しました。",
          subTitle: menuNames.join(`\n`),
          nextButtonLabel: "閉じる",
          handleNext: () => dispatch(appActions.closeModal()),
        }),
      );
    });
  }, [order]);

  const handleGoToTop = useCallback(() => {
    checkCampaign({
      fetchOnly: true,
      onNoCampaign: () => navigateToTopPage(),
      onHasCampaign: () => navigateToPage(PageRoutes.CAMPAIGN_SELECTION),
    });
  }, [checkCampaign, navigateToTopPage, navigateToPage]);

  return {
    order,
    store,
    orderId,
    pickupDateText,
    campaign,
    isStockItemMode,
    handleSetFavoriteStore,
    handleSetFavoriteMenu,
    handleGoToTop,
  };
};
